import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Button,
  Link,
  StatusIndicator,
  Box,
  SpaceBetween
} from '@cloudscape-design/components';
import { blissApi } from './blissApi.js';

/**
 * Truncate a filename from the middle, keeping start and end visible.
 * e.g. "starting file name aaaaaaa asdfg.csv" → "starting file...asdfg.csv"
 */
function truncateMiddle(str, maxLen = 28) {
  if (!str || str.length <= maxLen) return str;
  const ext = str.lastIndexOf('.') > 0 ? str.slice(str.lastIndexOf('.')) : '';
  const nameWithoutExt = str.slice(0, str.length - ext.length);
  const keep = maxLen - ext.length - 3; // 3 for "..."
  if (keep <= 4) return str.slice(0, maxLen - 3) + '...';
  const front = Math.ceil(keep * 0.6);
  const back = keep - front;
  return nameWithoutExt.slice(0, front) + '...' + nameWithoutExt.slice(-back) + ext;
}

export default function RecentJobs({ refreshTrigger }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await blissApi.getRecentJobs();
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Error loading jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [refreshTrigger]);

  const getStatusIndicator = (status) => {
    const map = {
      'RUNNING': { type: 'in-progress', label: 'Running' },
      'SUCCEEDED': { type: 'success', label: 'Succeeded' },
      'FAILED': { type: 'error', label: 'Failed' },
      'STOPPED': { type: 'stopped', label: 'Stopped' },
      'TIMEOUT': { type: 'error', label: 'Timeout' },
    };
    const config = map[status] || { type: 'pending', label: status };
    return <StatusIndicator type={config.type}>{config.label}</StatusIndicator>;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const handleDownload = async (jobRunId) => {
    try {
      await blissApi.downloadJobOutput(jobRunId);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownloadInput = async (inputFile) => {
    try {
      await blissApi.downloadFile(`lead-enrichment/input/${inputFile}`, inputFile);
    } catch (err) {
      alert(err.message);
    }
  };

  const columnDefinitions = [
    {
      id: 'input_file',
      header: 'Input File',
      cell: item => {
        const filename = item.input_file || '-';
        if (filename === '-' || filename === 'Unknown') return filename;
        // Strip timestamp prefix (YYYYMMDD_HHMMSS_) from display
        const displayName = filename.replace(/^\d{8}_\d{6}_/, '');
        return (
          <Link onFollow={() => handleDownloadInput(filename)} fontSize="body-s" title={filename}>
            {truncateMiddle(displayName, 40)}
          </Link>
        );
      },
      isRowHeader: false,
      width: 140,
    },
    {
      id: 'leads',
      header: 'Leads',
      cell: item => item.leads_processed || '-',
      width: 70,
    },
    {
      id: 'timestamp',
      header: 'Triggered',
      cell: item => formatTime(item.started_on),
      width: 130,
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: item => item.duration_mins ? `${item.duration_mins} min` : '-',
      width: 90,
    },
    {
      id: 'tokens',
      header: 'Usage',
      cell: item => {
        const usage = item.token_usage || '-';
        if (usage === '-') return '\u2014';

        const parts = usage.split('/').map(s => parseInt(s.trim().replace(/,/g, ''), 10));
        if (parts.length !== 2 || isNaN(parts[0])) return usage;

        const inputTokens = parts[0];
        const outputTokens = parts[1];
        const total = inputTokens + outputTokens;

        const inputRate = 0.003;
        const outputRate = 0.015;
        const inputCost = (inputTokens / 1000) * inputRate;
        const outputCost = (outputTokens / 1000) * outputRate;
        const totalCost = inputCost + outputCost;

        const formatK = (n) => n >= 1000 ? `${Math.round(n / 1000)}K` : n.toLocaleString();
        const costDisplay = totalCost >= 0.01 ? `$${totalCost.toFixed(2)}` : totalCost > 0 ? `$${totalCost.toFixed(4)}` : '$0';

        return (
          <span
            className="bliss-usage-trigger"
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector('.bliss-usage-tooltip');
              if (tooltip) {
                const rect = e.currentTarget.getBoundingClientRect();
                tooltip.style.top = `${rect.bottom + 4}px`;
                tooltip.style.left = `${rect.left}px`;
              }
            }}
          >
            {formatK(total)} ({costDisplay})
            <span className="bliss-usage-tooltip">
              Model: Claude Sonnet<br/>
              Input: {inputTokens.toLocaleString()} tokens × ${inputRate}/1K = ${inputCost.toFixed(4)}<br/>
              Output: {outputTokens.toLocaleString()} tokens × ${outputRate}/1K = ${outputCost.toFixed(4)}<br/>
              Total: {total.toLocaleString()} tokens<br/>
              Est. cost: {costDisplay}
            </span>
          </span>
        );
      },
      width: 130,
    },
    {
      id: 'user',
      header: 'User',
      cell: item => {
        const email = item.user_email || 'Unknown';
        return email.includes('@') ? email.split('@')[0] : email;
      },
      width: 100,
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => getStatusIndicator(item.status),
      width: 130,
    },
    {
      id: 'reports',
      header: 'Sample Reports',
      cell: item => item.status === 'SUCCEEDED' ? <ReportLinks jobRunId={item.job_run_id} /> : null,
      width: 180,
    },
    {
      id: 'actions',
      header: 'Download',
      cell: item => (
        item.status === 'SUCCEEDED' && item.output_file ? (
          <Button
            variant="inline-link"
            iconName="download"
            onClick={() => handleDownload(item.job_run_id)}
          >
            CSV
          </Button>
        ) : null
      ),
      width: 80,
    },
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Last 20 enrichment runs"
          actions={
            <Button iconName="refresh" onClick={loadJobs} loading={loading}>
              Refresh
            </Button>
          }
        >
          📋 Recent Jobs
        </Header>
      }
    >
      <Table
        columnDefinitions={columnDefinitions}
        items={jobs}
        loading={loading}
        loadingText="Loading jobs..."
        variant="embedded"
        sortingDisabled
        empty={
          <Box textAlign="center" color="inherit" padding="l">
            <SpaceBetween size="xxs">
              <Box variant="p" color="inherit"><b>No jobs yet</b></Box>
              <Box variant="p" color="text-body-secondary">Upload a CSV to start your first enrichment run.</Box>
            </SpaceBetween>
          </Box>
        }
      />
    </Container>
  );
}


/**
 * Inline component that lazily loads sample report links for a job row.
 */
function ReportLinks({ jobRunId }) {
  const [reports, setReports] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    blissApi.getJobReports(jobRunId).then(data => {
      if (!cancelled) {
        setReports(data.reports || []);
        setLoaded(true);
      }
    }).catch(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [jobRunId]);

  if (!loaded) return <Box color="text-body-secondary" fontSize="body-s">Loading...</Box>;
  if (!reports || reports.length === 0) return <Box color="text-body-secondary" fontSize="body-s">-</Box>;

  return (
    <SpaceBetween size="xxxs" direction="vertical">
      {reports.map((r, i) => (
        <Link key={i} href={r.url} external fontSize="body-s">
          {r.company}
        </Link>
      ))}
    </SpaceBetween>
  );
}
